import { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import dayjs, { Dayjs } from 'dayjs';
import { Box, ClosableDrawer, Grid } from '@atoms/layout';
import { useDirectQualificationsStore, useOrgQualificationsStore, useOrgSpecialtiesStore } from '@libs/store/settings';
import { Chip, FormControlLabel, IconButton, Stack, styled, Switch, Typography } from '@mui/material';
import { Button } from '@atoms/buttons';
import { useOrgDepartmentsStore } from '@libs/store/settings/departments';
import { useForm } from 'react-hook-form';
import {
  CollapsibleFormBlock,
  FormBlock,
  FormCheckbox,
  FormDatePicker,
  FormNumberField,
  FormRadioGroup,
  FormSelect,
  FormTextField,
  FormTimePicker,
  Form,
} from '@molecules/form';
import { useLocationStore } from '@libs/store/locations';
import { SingleShift } from '@libs/models/shifts/shift';
import { useNotification } from '@libs/snackbar';
import { Schedule, ShiftSeriesCreate } from '@libs/models/shifts/series';
import { TimePicker } from '@atoms/inputs';

import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import { WeekDay } from '@libs/models/common';
import { parseError } from '@libs/api/errors';
import {
  useShareDrawerStore,
  useShiftDrawerTalents,
  useShiftsAssignment,
  useShiftSeriesAddEditStore,
} from '@libs/store/shifts';
import { TIMEZONE_FALLBACK } from '@libs/config';
import { useShiftTemplatesList } from '@libs/store/shiftTemplates';
import { useShiftTypePresetsList } from '@libs/store/shiftTypePresets';
import { AssigneesSelect, QualificationSelect } from './components';
import { LocationFormData, LocationSelectionSection } from '../components';
import { DrawerControlButton } from '@molecules/drawers';

type EditSeriesDrawerProps = {
  onSaveCall?: () => void;
};

export const WeekVariants: Record<string, Record<WeekDay, Schedule[]>> = {
  Saturday: {
    Saturday: [],
    Sunday: [],
    Monday: [],
    Tuesday: [],
    Wednesday: [],
    Thursday: [],
    Friday: [],
  },
  Sunday: {
    Sunday: [],
    Monday: [],
    Tuesday: [],
    Wednesday: [],
    Thursday: [],
    Friday: [],
    Saturday: [],
  },
  Monday: {
    Monday: [],
    Tuesday: [],
    Wednesday: [],
    Thursday: [],
    Friday: [],
    Saturday: [],
    Sunday: [],
  },
};

// TODO: rename to `Create` because for the editing we use another UI
export function EditSeriesDrawer({ onSaveCall }: EditSeriesDrawerProps) {
  const intl = useIntl();
  const { showErrorIntl } = useNotification();
  const openShiftShareDrawer = useShareDrawerStore((s) => s.openShiftShareDrawer);
  const { requestAssignShiftOnExternal } = useShiftsAssignment();
  const {
    drawerOpen,
    loading: seriesLoading,
    data: currentSeriesDetails,
    seriesId,
    closeShiftSeriesDrawer,
    load,
    drawerMode,
    createSeries,
  } = useShiftSeriesAddEditStore();
  const { loading: locationsLoading, currentLocationID, current: currentLocation, loadSimple } = useLocationStore();
  const {
    load: loadDirectQualifications,
    loading: isDirectQualListLoading,
    data: directQualData,
  } = useDirectQualificationsStore();
  const { orgQualifications, load: loadOrgQuals, loading: isOrgQualListLoading } = useOrgQualificationsStore();
  const { loadSpecialtiesList: loadSpecialties, orgSpecialties, loading: specialtiesLading } = useOrgSpecialtiesStore();
  const { departmentsTreeLoading } = useOrgDepartmentsStore();
  const { load: loadProviders, talents, loading: talentsLoading } = useShiftDrawerTalents();

  const {
    initialized: templatesListInitialized,
    load: loadShiftTemplates,
    list: shiftTemplates,
    deinit: deinitShiftTemplatesList,
  } = useShiftTemplatesList();

  const {
    initialized: shiftTypePresetsInitialized,
    load: loadShiftTypePresets,
    list: shiftTypePresets,
    deinit: deinitShiftTypePresetsList,
  } = useShiftTypePresetsList();

  const [showArchivedWarn, setShowArchivedWarn] = useState<boolean>(false);
  const [showSemiArchivedWarn, setShowSemiArchivedWarn] = useState<boolean>(false);

  const isAddDialog = drawerMode == 'add';
  const localizationSubset = isAddDialog ? 'shifts.addSeriesDrawer' : 'shifts.editSeriesDrawer';
  const open = drawerOpen;

  const loading =
    seriesLoading ||
    isOrgQualListLoading ||
    locationsLoading ||
    talentsLoading ||
    departmentsTreeLoading ||
    specialtiesLading ||
    isDirectQualListLoading ||
    !templatesListInitialized ||
    !shiftTypePresetsInitialized;

  useEffect(() => {
    deinitShiftTemplatesList();
    deinitShiftTypePresetsList();
  }, [deinitShiftTemplatesList, deinitShiftTypePresetsList]);

  useEffect(() => {
    if (!templatesListInitialized) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      loadShiftTemplates({
        location: {
          key: 'location',
          value: [currentLocationID],
        },
      });
    }
  }, [templatesListInitialized, loadShiftTemplates]);

  useEffect(() => {
    if (!shiftTypePresetsInitialized) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      loadShiftTypePresets({
        location: {
          key: 'location',
          value: [currentLocationID],
        },
      });
    }
  }, [shiftTypePresetsInitialized, loadShiftTypePresets]);

  useEffect(() => {
    if (!currentLocationID) {
      loadSimple();
    }
  }, [currentLocationID]);

  useEffect(() => {
    if (!orgQualifications) {
      loadOrgQuals();
    }
  }, [orgQualifications]);

  useEffect(() => {
    if (seriesId) {
      load(seriesId);
    }
  }, [seriesId]);

  useEffect(() => {
    setCurrentShift({
      ...currentShift,
      ...currentSeriesDetails,
      assignees: currentSeriesDetails?.assignees.map((a) => a.id) || [],
    });
  }, [currentSeriesDetails]);

  useEffect(() => {
    if (!directQualData || !directQualData.length) {
      loadDirectQualifications();
    }
  }, [loadDirectQualifications, directQualData]);

  const initial: ShiftSeriesCreate = {
    numberOfCopies: 0, // positive int
    assignees: [], // array of ids
    staffingType: 'All',
    startDate: dayjs(new Date()).toServerDateTimeFormat(), // here should be the date from server
    endDate: dayjs(new Date()).toServerDateTimeFormat(), // here should be the date from server
    schedule: [],
    departmentNodeId: '',
    specialtyId: '',
    locationId: '',
    timeZone: '',

    staffNurseQualificationFilterIds: [], // Qualification ids
    staffOtherQualificationFilterIds: [], // Qualification ids
    tags: [],
    note: '',
    isUrgent: false,
    subDepartmentNodeId: '',
    isNeedToPublish: false,
  };

  const [currentShift, setCurrentShift] = useState<ShiftSeriesCreate>({
    ...initial,
    ...(currentSeriesDetails || {}),
    assignees: currentSeriesDetails?.assignees.map((a) => a.id) || [],
  });
  const [scheduledTimeOfAWeek, setScheduledTimeOfAWeek] = useState<number>(0); // Seconds !!!

  const methods = useForm<ShiftSeriesFormData>({
    defaultValues: {
      template: '',
      numberOfCopies: 1,
      isUrgent: currentShift.isUrgent,

      durationType: 'setEndDate',
      startDate: null, //dayjs(currentShift.startDate),
      endDate: null, //dayjs(currentShift.endDate),

      seriesPreset: '',
      shiftTypePreset: '',
      startTime: null, //dayjs(currentShift.startDate),
      endTime: null, //dayjs(currentShift.startDate),

      schedule: WeekVariants[currentLocation?.workWeekDayStart || 'Monday'],

      location: currentLocationID || '',
      department: '',
      subDepartment: '',
      specialty: '',

      staffingType: 'All',
      qualifications: [],
      assignees: [],

      tags: '',
      notes: '',
    },
  });
  const { setValue, setError, watch } = methods;

  const durationType = watch('durationType');
  const scheduleList = watch('schedule');

  const endDate = watch('endDate');
  const startTime = watch('startTime');
  const endTime = watch('endTime');
  const defaultStartTime = startTime;
  const defaultEndTime = endTime;
  const startDate = watch('startDate');
  const assigneesIds = watch('assignees');
  const assigned = talents.filter((talent) => assigneesIds.includes(talent.id));
  const locationId = watch('location');
  const numberOfCopies = watch('numberOfCopies');
  const template = watch('template');

  useEffect(() => {
    if (template) {
      const selectedTemplate = shiftTemplates.find((t) => t.id === template);
      if (selectedTemplate) {
        setValue('durationInDays', selectedTemplate.durationInDays || null);
        setValue('startTime', dayjs(selectedTemplate.startTime, 'HH:mm:ss'));
        setValue(
          'endTime',
          dayjs(selectedTemplate.startTime, 'HH:mm:ss').add(selectedTemplate.durationInSeconds, 'seconds'),
        );
        const realDepartments = (currentLocation?.structure ?? []).filter(
          (dep) => dep.type === 'department' && currentLocation?.usedDepartmentNodes.includes(dep.id),
        );
        if (
          selectedTemplate.departmentNode?.id &&
          realDepartments.find((dep) => dep.id === selectedTemplate.departmentNode.id)
        ) {
          setValue('department', selectedTemplate.departmentNode.id);
          if (selectedTemplate.subDepartmentNode?.id) {
            setValue('subDepartment', selectedTemplate.subDepartmentNode.id);
            if (selectedTemplate.specialty?.id) {
              setValue('specialty', selectedTemplate.specialty.id);
            }
          }
        }
        if (selectedTemplate.shiftTimePresetTemplate?.id) {
          setValue('shiftTypePreset', selectedTemplate.shiftTimePresetTemplate.id);
        }
        if (selectedTemplate.qualifications?.length) {
          setValue(
            'qualifications',
            selectedTemplate.qualifications.map((q) => q.id),
          );
        }

        if (selectedTemplate.tags?.length) {
          setValue('tags', selectedTemplate.tags.join(', '));
        }
      }
    }
  }, [template, shiftTemplates, setValue]);

  const shiftTypePreset = watch('shiftTypePreset');

  useEffect(() => {
    if (shiftTypePreset) {
      const selectedPreset = shiftTypePresets.find((t) => t.id === shiftTypePreset);
      if (selectedPreset) {
        setValue('startTime', dayjs(selectedPreset.startTime, 'HH:mm:ss'));
        setValue(
          'endTime',
          dayjs(selectedPreset.startTime, 'HH:mm:ss').add(selectedPreset.durationInSeconds, 'seconds'),
        );
      }
    }
  }, [shiftTypePreset, setValue]);

  useEffect(() => {
    // TODO: use autocomplete
    loadProviders({
      filters: {
        locations: { key: 'locations', value: [locationId] },
      },
    });
  }, [loadProviders, locationId]);

  const handleClose = () => {
    closeShiftSeriesDrawer();
    methods.reset();
  };

  const handleSave = async () => {
    await methods.handleSubmit(handleSubmit.bind(handleSubmit, false, false))();
    onSaveCall && onSaveCall();
  };

  const handlePublish = async () => {
    await methods.handleSubmit(handleSubmit.bind(handleSubmit, true, false))();
    onSaveCall && onSaveCall();
  };
  const handlePublishNShare = async () => {
    await methods.handleSubmit(handleSubmit.bind(handleSubmit, true, true))();
    onSaveCall && onSaveCall();
  };

  const validateFormData = (data: ShiftSeriesFormData) => {
    if (data.endDate && data.startDate!.toDate() > data.endDate.toDate()) {
      setError('endDate', {
        message: intl.formatMessage({ id: 'shifts.addSeriesDrawer.dateAndDuration.endDate.endDateLessError' }),
        type: 'maxLength',
      });
      return false;
    }
    const schedule = Object.keys(scheduleList).reduce((acc, dayName) => {
      return [...acc, ...scheduleList[dayName as WeekDay]];
    }, [] as Schedule[]);

    if (schedule.length === 0) {
      showErrorIntl(`${localizationSubset}.errors.EmptySchedule`);
      return false;
    }

    if (schedule.some((item) => 0 >= item.durationInSeconds)) {
      showErrorIntl(`${localizationSubset}.errors.InvalidScheduleValues`);
      return false;
    }

    return true;
  };

  const handleSubmit = async (
    shouldPerformPublish: boolean,
    shouldOpenShareAfterPublish: boolean,
    data: ShiftSeriesFormData,
  ) => {
    if (!validateFormData(data)) {
      return;
    }
    const tagsParsed = (data.tags || '').split(',').map((tag) => tag.trim());
    const assignees = data.assignees.map((value) => talents.find((t) => t.id === value));
    const dataToSend: ShiftSeriesCreate = {
      numberOfCopies: parseInt('' + data.numberOfCopies, 10),
      assignees: assignees.filter((talent) => talent?.staffingType !== 'Agency').map((t) => t!.id),
      staffingType: data.staffingType,
      startDate: data.startDate!.toServerDateTimeFormat(),
      endDate: data.endDate?.toServerDateTimeFormat() || null,
      schedule: Object.keys(scheduleList).reduce((acc, dayName) => {
        return [...acc, ...scheduleList[dayName as WeekDay]];
      }, [] as Schedule[]),
      departmentNodeId: data.department,
      specialtyId: data.specialty,
      locationId: data.location,
      timeZone: currentLocation?.timezone ?? TIMEZONE_FALLBACK,
      staffNurseQualificationFilterIds: data.qualifications,
      staffOtherQualificationFilterIds: [],
      tags: tagsParsed,
      note: data.notes || '',
      isUrgent: !!data.isUrgent,
      subDepartmentNodeId: data.subDepartment,
      isNeedToPublish: shouldPerformPublish,
    };
    try {
      const listOfSeries = await createSeries(dataToSend);
      if (shouldPerformPublish && !shouldOpenShareAfterPublish && listOfSeries && listOfSeries.length) {
        for (const assignee of assignees) {
          if (assignee && assignee.staffingType == 'Agency') {
            await requestAssignShiftOnExternal(listOfSeries[0], assignee.id);
          }
        }
      }
      if (shouldOpenShareAfterPublish && listOfSeries.length) {
        openShiftShareDrawer({
          preselectedRecurrences: listOfSeries,
        });
      }
      handleClose();
    } catch (error) {
      showErrorIntl(parseError(error, `${localizationSubset}.errors`).message);
    }
  };

  const handleCancel = handleClose;

  const handleScheduleDayChange = (dayMark: WeekDay, dayRangeSet: TimeRangeRecord[]) => {
    scheduleList[dayMark] = dayRangeSet.reduce<Schedule[]>((acc, range) => {
      let duration = 0;
      if (range.disabled) {
        return acc;
      }
      if (range.start && range.end) {
        if (range.start.toDate() > range.end.toDate()) {
          duration = dayjs(range.end.add(1, 'day')).diff(range.start) / 1000;
        } else {
          duration = dayjs(range.end).diff(range.start) / 1000;
        }
      }
      acc.push({
        dayOfWeek: dayMark,
        startTime: range.start?.format('HH:mm:ss') || '00:00:00',
        durationInSeconds: duration,
      });

      return acc;
    }, []);

    setValue('schedule', scheduleList);
  };
  useEffect(() => {
    const timeAtWeek: number = Object.keys(scheduleList).reduce((acc, dayName) => {
      const timeOfDay = scheduleList[dayName as WeekDay].reduce((acc, timeset) => {
        const timeOfSection = timeset.durationInSeconds;
        return acc + (timeOfSection || 0);
      }, 0);
      return acc + timeOfDay;
    }, 0);
    setScheduledTimeOfAWeek(timeAtWeek);
  }, [scheduleList]);

  useEffect(() => {
    if (currentLocationID) {
      // Set value from current location
      setValue('location', currentLocationID);
    }
  }, [setValue, currentLocationID]);

  useEffect(() => {
    if (startDate && endDate && startDate?.toDate() > endDate?.toDate()) {
      setValue('endDate', startDate);
    }
  }, [startDate, endDate]);

  const minimalDate = dayjs(new Date()).subtract(1, 'year').startOf('day');
  const maxDate = dayjs('2099-12-31').startOf('day');

  useEffect(() => {
    if (endDate && endDate?.toDate() > maxDate.toDate()) {
      setValue('endDate', maxDate);
    }
  }, [endDate]);

  useEffect(() => {
    if (startDate && startDate?.toDate() > maxDate.toDate()) {
      setValue('startDate', maxDate);
    }
    const durationInDays = watch('durationInDays');
    if (startDate && durationInDays) {
      const newEndDate = startDate.clone().add(durationInDays, 'day');
      if (endDate && endDate.toDate() < newEndDate.toDate()) {
        setValue('endDate', newEndDate);
      } else {
        setValue('endDate', newEndDate);
      }
    }
  }, [setValue, startDate]);

  let daysRange: string[] | null = null;

  if (startDate && endDate) {
    let numberOfDaysInRange = dayjs.duration(endDate.diff(startDate)).asDays();
    if (!numberOfDaysInRange || numberOfDaysInRange < 0) {
      numberOfDaysInRange = 0;
    }
    if (numberOfDaysInRange > 7 || durationType === 'infiniteLoop') {
      numberOfDaysInRange = 7;
    }
    daysRange = new Array(Math.ceil(numberOfDaysInRange) + 1)
      .fill('')
      .map((_, idx) => startDate.add(idx, 'days').format('dddd'));
  }

  useEffect(() => {
    if (startDate && endDate) {
      const isArchived = dayjs(startDate).isBefore(dayjs(), 'day') && dayjs(endDate).isBefore(dayjs(), 'day');
      const isSemiArchived =
        dayjs(startDate).isBefore(dayjs(), 'day') && dayjs(endDate).isAfter(dayjs().subtract(1, 'day'), 'day');
      setShowArchivedWarn(isArchived);
      setShowSemiArchivedWarn(isSemiArchived);
    }
  }, [startDate, endDate]);

  const assignee = methods.getValues('assignees');
  const shouldShowPublishNShareButton = isAddDialog;
  const shouldDisablePublishNShare = loading || assignee.length !== 0;

  return (
    <ClosableDrawer
      open={open}
      loading={loading}
      showXMarkButton={false}
      onClose={handleClose}
      slots={{
        header: (
          <Stack direction={'row'}>
            <Typography variant="h5">
              <FormattedMessage id={`${localizationSubset}.dialogHeading`} />
            </Typography>
            <Box flex={1} />
          </Stack>
        ),
      }}
      controls={
        <Stack direction={'row-reverse'} spacing={2} sx={(theme) => ({ paddingBottom: theme.spacing(1) })}>
          {shouldShowPublishNShareButton && (
            <DrawerControlButton
              variant="contained"
              onClick={handlePublishNShare}
              disabled={shouldDisablePublishNShare}
            >
              <FormattedMessage id={`${localizationSubset}.menuButtons.publishNShare.label`} />
            </DrawerControlButton>
          )}
          <MenuButton variant="contained" onClick={handlePublish} disabled={loading}>
            <FormattedMessage id={`${localizationSubset}.menuButtons.publish.label`} />
          </MenuButton>
          <MenuButton variant="outlined" onClick={handleSave} disabled={loading}>
            <FormattedMessage id="common.save" />
          </MenuButton>
          <Box flex={1} />
          <MenuButton
            onClick={handleCancel}
            sx={(theme) => ({ color: theme.palette.text.secondary })}
            disabled={loading}
          >
            <FormattedMessage id={`${localizationSubset}.menuButtons.back.label`} />
          </MenuButton>
        </Stack>
      }
    >
      <Stack direction={'column'} sx={{ height: '100%' }} spacing={2} width={{ xs: '300px', sm: '600px' }}>
        <Form formUtils={methods} style={{ height: '100%' }} onSubmit={handleSubmit.bind(handleSubmit, false, false)}>
          {/* Shift configuration */}
          <FormBlock title={<FormattedMessage id={`${localizationSubset}.shiftConfiguration.sectTitle`} />}>
            <Stack direction={'row'} spacing={2}>
              <FormSelect
                disabled={loading || !isAddDialog}
                label={<FormattedMessage id={`${localizationSubset}.shiftConfiguration.shiftTemplate.label`} />}
                placeholder={intl.formatMessage({
                  id: `${localizationSubset}.shiftConfiguration.shiftTemplate.placeholder`,
                })}
                fullWidth
                name="template"
                options={shiftTemplates
                  .filter((t) => t.recurrenceType === 'series')
                  .map((t) => ({
                    key: t.id,
                    label: t.name,
                    value: t.id,
                  }))}
              />
              <FormNumberField
                disabled={loading || !isAddDialog}
                label={<FormattedMessage id={`${localizationSubset}.shiftConfiguration.quantity.label`} />}
                fullWidth
                minValue={1}
                name="numberOfCopies"
              />
            </Stack>
            <FormCheckbox
              name="isUrgent"
              label={<FormattedMessage id={`${localizationSubset}.shiftConfiguration.markAsUrgent.label`} />}
              size="medium"
            />
          </FormBlock>

          {/* Date and duration */}
          <FormBlock title={<FormattedMessage id={`${localizationSubset}.dateAndDuration.sectTitle`} />}>
            <Stack direction={'column'} spacing={1}>
              <FormRadioGroup
                name="durationType"
                label={<FormattedMessage id={`${localizationSubset}.dateAndDuration.durationType.label`} />}
                items={[
                  {
                    label: (
                      <FormattedMessage id={`${localizationSubset}.dateAndDuration.durationType.items.setEndDate`} />
                    ),
                    value: 'setEndDate',
                  },
                  {
                    label: (
                      <FormattedMessage id={`${localizationSubset}.dateAndDuration.durationType.items.infiniteLoop`} />
                    ),
                    value: 'infiniteLoop',
                    disabled: true,
                  },
                ]}
                required
                disabled={loading}
                row
              />
              {/* <Stack direction={'row'} spacing={2}> */}
              <Grid container direction={'row'}>
                <Grid xs={6} sx={{ paddingRight: '8px' }}>
                  <FormDatePicker
                    disabled={loading}
                    label={<FormattedMessage id={`${localizationSubset}.dateAndDuration.startDate.label`} />}
                    fullWidth
                    minDate={minimalDate}
                    maxDate={maxDate}
                    name="startDate"
                    defaultValue={null}
                    // disablePast
                    required
                  />
                </Grid>
                <Grid xs={6} sx={{ paddingLeft: '8px' }}>
                  {durationType === 'setEndDate' && (
                    <FormDatePicker
                      disabled={loading}
                      label={<FormattedMessage id={`${localizationSubset}.dateAndDuration.endDate.label`} />}
                      fullWidth
                      name="endDate"
                      minDate={dayjs(startDate || minimalDate)}
                      maxDate={maxDate}
                      // disablePast
                      defaultValue={null}
                      required
                    />
                  )}
                </Grid>
                {showArchivedWarn && (
                  <Grid xs={12} sx={{ paddingLeft: '8px' }}>
                    <Typography variant="caption" sx={(theme) => ({ color: theme.palette.warning.main })}>
                      <FormattedMessage id={`${localizationSubset}.dateAndDuration.archivedWarning.label`} />
                    </Typography>
                  </Grid>
                )}
                {showSemiArchivedWarn && (
                  <Grid xs={12} sx={{ paddingLeft: '8px' }}>
                    <Typography variant="caption" sx={(theme) => ({ color: theme.palette.warning.main })}>
                      <FormattedMessage id={`${localizationSubset}.dateAndDuration.semiArchivedWarning.label`} />
                    </Typography>
                  </Grid>
                )}
              </Grid>
              {/* </Stack> */}
            </Stack>
          </FormBlock>
          {/* Series details */}
          <FormBlock title={<FormattedMessage id={`${localizationSubset}.seriesDetails.sectTitle`} />}>
            <Grid container spacing={2}>
              {/* <Grid xs={6}>
                <FormSelect
                  disabled
                  label={<FormattedMessage id={`${localizationSubset}.seriesDetails.seriesPreset.label`} />}
                  placeholder={intl.formatMessage({
                    id: `${localizationSubset}.seriesDetails.seriesPreset.placeholder`,
                  })}
                  fullWidth
                  name="seriesPreset"
                  options={[]}
                />
              </Grid> */}
              <Grid xs={6}>
                <FormSelect
                  disabled={loading || !isAddDialog}
                  label={<FormattedMessage id={`${localizationSubset}.seriesDetails.shiftTypePreset.label`} />}
                  placeholder={intl.formatMessage({
                    id: `${localizationSubset}.seriesDetails.shiftTypePreset.placeholder`,
                  })}
                  fullWidth
                  name="shiftTypePreset"
                  options={shiftTypePresets.map((t) => ({
                    key: t.id,
                    label: t.name,
                    value: t.id,
                  }))}
                />
              </Grid>
              <Grid xs={6}></Grid>
              <Grid xs={6}>
                <FormTimePicker
                  disabled={loading}
                  label={<FormattedMessage id={`${localizationSubset}.seriesDetails.startTime.label`} />}
                  fullWidth
                  name="startTime"
                  defaultValue={null}
                  required
                />
              </Grid>
              <Grid xs={6}>
                <FormTimePicker
                  disabled={loading}
                  label={<FormattedMessage id={`${localizationSubset}.seriesDetails.endTime.label`} />}
                  fullWidth
                  name="endTime"
                  defaultValue={null}
                  required
                />
              </Grid>
            </Grid>
          </FormBlock>
          {/* Series schedule */}
          <CollapsibleFormBlock
            expanded={true}
            sectTitle={
              <>
                <FormattedMessage id={`${localizationSubset}.seriesSchedule.sectTitle`} />
                <Box sx={(theme) => ({ color: theme.palette.error.main, marginLeft: '3px' })}>*</Box>
              </>
            }
            sectTitleBadges={
              <Chip
                size="small"
                label={intl.formatMessage(
                  { id: `${localizationSubset}.seriesSchedule.sectBadges.spendTime` },
                  { amount: dayjs.duration(scheduledTimeOfAWeek, 'seconds').humanize() },
                )}
              />
            }
          >
            <Stack direction={'column'} spacing={2}>
              {Object.keys(scheduleList).map((dayMark: string, idx) => {
                return (
                  <DayScheduleSection
                    key={idx}
                    day={dayMark}
                    timeSet={scheduleList[dayMark as WeekDay].map((s) => ({
                      start: s.startTime ? dayjs(s.startTime, 'HH:mm:ss') : null,
                      end: s.durationInSeconds
                        ? dayjs(s.startTime, 'HH:mm:ss').add(s.durationInSeconds, 'seconds')
                        : null,
                      disabled: !s.durationInSeconds,
                    }))}
                    disabled={!daysRange?.includes(dayMark) || !startTime || !endTime}
                    defaultStartTime={defaultStartTime}
                    defaultEndTime={defaultEndTime}
                    localizationSubset={`${localizationSubset}.seriesSchedule.daySchedule`}
                    onChange={(dayRangeSet) => {
                      handleScheduleDayChange(dayMark as WeekDay, dayRangeSet);
                    }}
                  />
                );
              })}
            </Stack>
            {/* <Grid container>
              <Grid xs={6}>
                <Stack direction={'column'} sx={{ height: '100%' }}>
                  <Box flex={1} />
                  <FormCheckbox
                    name="shouldSavePreset"
                    disabled
                    label={
                      <FormattedMessage id={`${localizationSubset}.seriesSchedule.preset.seriesPresetCheckbox.label`} />
                    }
                    size="large"
                  />
                </Stack>
              </Grid>
              <Grid xs={6}>
                <FormTextField
                  disabled={true} //{loading}
                  label={<FormattedMessage id={`${localizationSubset}.seriesSchedule.preset.presetName.label`} />}
                  placeholder={intl.formatMessage({
                    id: `${localizationSubset}.seriesSchedule.preset.presetName.placeholder`,
                  })}
                  fullWidth
                  name="presetName"
                />
              </Grid>
            </Grid> */}
          </CollapsibleFormBlock>
          {/* Location */}
          <FormBlock title={<FormattedMessage id={`${localizationSubset}.location.sectTitle`} />}>
            <LocationSelectionSection localizationSubset={localizationSubset} loading={loading} />
          </FormBlock>

          {/* Talent */}
          <FormBlock title={<FormattedMessage id={`${localizationSubset}.talentConfiguration.sectTitle`} />}>
            <Grid container spacing={2}>
              <Grid xs={12}>
                <FormRadioGroup
                  name="staffingType"
                  label={<FormattedMessage id={`${localizationSubset}.talentConfiguration.talentType.label`} />}
                  items={[
                    {
                      label: <FormattedMessage id={`${localizationSubset}.talentConfiguration.talentType.items.all`} />,
                      value: 'All',
                    },
                    {
                      label: (
                        <FormattedMessage id={`${localizationSubset}.talentConfiguration.talentType.items.internal`} />
                      ),
                      value: 'Internal',
                    },
                    {
                      label: (
                        <FormattedMessage id={`${localizationSubset}.talentConfiguration.talentType.items.external`} />
                      ),
                      value: 'External',
                    },
                  ]}
                  required
                  disabled={loading}
                  row
                />
              </Grid>
              <Grid xs={12}>
                <QualificationSelect
                  fullWidth
                  name="qualifications"
                  required
                  localizationSubset={localizationSubset}
                  disabledOptions={assigned.flatMap((a) => a.qualifications.map((q) => q.id))}
                />
              </Grid>
              <Grid xs={12}>
                <AssigneesSelect
                  fullWidth
                  name="assignees"
                  numberOfCopies={numberOfCopies}
                  localizationSubset={localizationSubset}
                  selectedQualifications={watch('qualifications')}
                  disabled={watch('qualifications').length === 0}
                />
              </Grid>
            </Grid>
          </FormBlock>

          {/* Additional info */}
          <FormBlock title={<FormattedMessage id={`${localizationSubset}.additionalInfo.sectTitle`} />}>
            <Grid container spacing={2}>
              <Grid xs={6}>
                <FormTextField
                  disabled={loading}
                  label={<FormattedMessage id={`${localizationSubset}.additionalInfo.tag.label`} />}
                  placeholder={intl.formatMessage({
                    id: `${localizationSubset}.additionalInfo.tag.placeholder`,
                  })}
                  fullWidth
                  name="tags"
                />
              </Grid>
            </Grid>
          </FormBlock>
          <CollapsibleFormBlock
            sectTitle={<FormattedMessage id={`${localizationSubset}.additionalInfo.notes.sectTitle`} />}
          >
            <FormTextField
              name="notes"
              fullWidth
              multiline
              disabled={loading}
              options={{
                maxLength: 300,
              }}
              InputProps={{
                sx: { height: '150px', overflow: 'hidden' },
              }}
            />
          </CollapsibleFormBlock>
        </Form>
      </Stack>
    </ClosableDrawer>
  );
}

export type TimeRangeRecord = {
  start: Dayjs | null;
  end: Dayjs | null;
  disabled: boolean;
};

type DayScheduleSectionProps = {
  day: string;
  localizationSubset: string;
  onChange: (rangeSet: TimeRangeRecord[]) => void;
  defaultStartTime?: Dayjs | null;
  defaultEndTime?: Dayjs | null;
  disabled: boolean;
  checked?: boolean;
  timeSet?: TimeRangeRecord[];
};

export function DayScheduleSection({
  day,
  localizationSubset,
  onChange,
  defaultStartTime = null,
  defaultEndTime = null,
  disabled = false,
  timeSet: defaultTimeSet = [],
}: DayScheduleSectionProps) {
  const [enabled, setEnabled] = useState<boolean>(false);
  const [timeSet, setTimeSet] = useState<TimeRangeRecord[]>([]);
  const intl = useIntl();

  useEffect(() => {
    if (timeSet.length === 0) {
      setTimeSet(defaultTimeSet);
      setEnabled(!!defaultTimeSet.some((ts) => !ts.disabled));
    }
  }, [defaultTimeSet]);

  useEffect(() => {
    onChange && onChange([...timeSet].map((ts) => ({ ...ts, disabled: !enabled })));
  }, [enabled]);

  const handleEnabled = (evt: React.SyntheticEvent | null, value: boolean) => {
    if (!timeSet.length && defaultStartTime && defaultEndTime) {
      const newVer = [...timeSet, { start: defaultStartTime, end: defaultEndTime, disabled: enabled }];
      setTimeSet(newVer);
      onChange && onChange(newVer);
    } else {
      const newVer = [...timeSet].map((ts) => ({ ...ts, disabled: !value }));
      onChange && onChange(newVer);
    }
    setEnabled(value);
  };

  const handleDelete = (index: number) => {
    const newVer = [...timeSet];
    newVer.splice(index, 1);
    setTimeSet(newVer);
    onChange && onChange(newVer);
  };

  const handleAdd = () => {
    const newVer = [...timeSet, { start: defaultStartTime, end: defaultEndTime, disabled: !enabled }];
    setTimeSet(newVer);
    onChange && onChange(newVer);
  };

  const handleCopy = () => {};

  const handleStartChange = (startTime: Dayjs | null, elementIndex: number) => {
    const newVer = [...timeSet];
    const changing = newVer[elementIndex];
    newVer[elementIndex] = { ...changing, start: startTime, disabled: !enabled };
    setTimeSet(newVer);
    onChange && onChange(newVer);
  };

  const handleEndChange = (endTime: Dayjs | null, elementIndex: number) => {
    const newVer = [...timeSet];
    const changing = newVer[elementIndex];
    newVer[elementIndex] = { ...changing, end: endTime, disabled: !enabled };
    setTimeSet(newVer);
    onChange && onChange(newVer);
  };

  return (
    <Grid container direction={'row'}>
      <Grid xs={3}>
        <FormControlLabel
          onChange={handleEnabled}
          value={enabled}
          disabled={disabled}
          control={<Switch checked={enabled} />}
          label={intl.formatMessage({ id: `common.weekDays.${day.toLowerCase()}` })}
        />
      </Grid>
      <Grid xs={9} spacing={2}>
        {enabled && (
          <Stack direction={'column'} spacing={2}>
            {timeSet.map((range, idx) => (
              <Stack direction={'row'} spacing={2} key={idx}>
                <TimePicker
                  fullWidth
                  defaultValue={defaultStartTime}
                  value={range.start}
                  onChange={(value) => {
                    handleStartChange(value, idx);
                  }}
                />
                <TimePicker
                  fullWidth
                  defaultValue={defaultEndTime}
                  value={range.end}
                  onChange={(value) => {
                    handleEndChange(value, idx);
                  }}
                />
                <Stack direction={'row'} spacing={0}>
                  {idx === 0 ? (
                    <HoverAbleIconButton size="small" onClick={handleAdd}>
                      <AddIcon />
                    </HoverAbleIconButton>
                  ) : (
                    <HoverAbleIconButton
                      color="error"
                      size="small"
                      onClick={() => {
                        handleDelete(idx);
                      }}
                    >
                      <DeleteOutlineIcon />
                    </HoverAbleIconButton>
                  )}
                  {idx === 0 ? (
                    <IconButton size="small" onClick={handleCopy} disabled>
                      <ContentCopyIcon />
                    </IconButton>
                  ) : (
                    <Box sx={{ width: '32px' }} />
                  )}
                </Stack>
              </Stack>
            ))}
          </Stack>
        )}
      </Grid>
    </Grid>
  );
}

const HoverAbleIconButton = styled(IconButton)(({ theme }) => ({
  opacity: 0,
  ':hover': {
    opacity: 1,
  },
}));

const MenuButton = styled(Button)(({ theme }) => ({
  minWidth: theme.spacing(16.75),
}));

type ShiftSeriesFormData = LocationFormData & {
  template: string;
  numberOfCopies: number;
  isUrgent: boolean;

  durationType: 'setEndDate' | 'infiniteLoop';
  durationInDays: number | null;
  startDate: Dayjs | null;
  endDate: Dayjs | null;

  seriesPreset: string;
  shiftTypePreset: string;
  startTime: Dayjs | null;
  endTime: Dayjs | null;

  schedule: Record<WeekDay, Schedule[]>;

  staffingType: SingleShift['staffingType'];
  qualifications: string[];
  assignees: string[];

  tags: string;
  notes: string;
};
